import React, { useState, useEffect } from 'react';
import { deleteDoc, doc, updateDoc, collection, getDocs } from '../../lib/auditedFirestore'; 
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { logAuditAction } from '../../lib/auditLogger'; 

// Components
import AddUserForm from './components/AddUserForm';
import BulkAddStudents from './components/BulkAddStudents';
import UserList from './components/UserList';
import AuditTrail from './components/AuditTrail'; 
import PaperListAdmin from './PaperListAdmin';
import ExamListAdmin from './ExamListAdmin';
import CreateExam from './CreateExam';
import ExamAttemptsList from './ExamAttemptsList';
import ExamAnalytics from '../exam/ExamAnalytics';
import QuestionBankManager from './QuestionBankManager';
import LectureManager from '../lecture/LectureManager';
import LectureRoom from '../lecture/LectureRoom';
import HomeworkManager from '../homework/HomeworkManager';
import HomeworkRoom from '../homework/HomeworkRoom';
import InstilIQManager from './InstilIQManager'; // Added Component Import

// --- Admin Permission Manager Component ---
const AdminPermissionManager = ({ adminUser, onClose, setModal }) => {
    const AVAILABLE_FEATURES = [
        { id: 'manage_students', label: 'Manage Students' },
        { id: 'evaluate_answers', label: 'Evaluate Descriptive Answers' },
        { id: 'view_reports', label: 'View Student Reports' },
        { id: 'manage_exams', label: 'Manage Exams & Content' },
        { id: 'view_audit_trail', label: 'View & review the platform audit trail (all papers)' }
    ];

    const [availablePapers, setAvailablePapers] = useState([]);
    const [allowedFeatures, setAllowedFeatures] = useState([...new Set([...(adminUser?.allowedFeatures || ['manage_students','evaluate_answers','view_reports','manage_exams']),...(adminUser?.canViewAuditLogs ? ['view_audit_trail'] : [])])]);
    const [assignedPapers, setAssignedPapers] = useState(adminUser?.assignedPapers || []);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchPapers = async () => {
            const snapshot = await getDocs(collection(db, 'papers'));
            const papersList = snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name }));
            setAvailablePapers(papersList);
        };
        fetchPapers();
    }, []);

    const toggleFeature = (featureId) => {
        setAllowedFeatures(prev => 
            prev.includes(featureId) ? prev.filter(id => id !== featureId) : [...prev, featureId]
        );
    };

    const togglePaper = (paperId) => {
        setAssignedPapers(prev => 
            prev.includes(paperId) ? prev.filter(id => id !== paperId) : [...prev, paperId]
        );
    };

    const handleSavePermissions = async () => {
        setLoading(true);
        try {
            const adminId = adminUser.id || adminUser.uid; 
            const adminRef = doc(db, 'users', adminId);
            
            await updateDoc(adminRef, {
                allowedFeatures: allowedFeatures,
                canViewAuditLogs: allowedFeatures.includes('view_audit_trail'),
                assignedPapers: assignedPapers
            });

            setModal({ show: true, message: `Permissions updated for ${adminUser.name || adminUser.email}` });
            onClose(); 
        } catch (error) {
            console.error("Error updating permissions:", error);
            setModal({ show: true, message: `Failed to update permissions: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md border border-gray-200 relative animate-fade-in">
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800">
                ✕ Close
            </button>
            <h2 className="text-xl font-bold mb-1 text-purple-700">Edit Permissions</h2>
            <p className="text-sm text-gray-500 mb-6">Modifying access for: <span className="font-semibold text-gray-800">{adminUser.email}</span></p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-gray-50 p-4 rounded-lg border">
                    <h3 className="font-semibold mb-3 border-b pb-2">Allowed Modules</h3>
                    <div className="flex flex-col gap-3">
                        {AVAILABLE_FEATURES.map(feature => (
                            <label key={feature.id} className="flex items-center gap-3 cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    checked={allowedFeatures.includes(feature.id)}
                                    onChange={() => toggleFeature(feature.id)}
                                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                                />
                                <span className="text-gray-700">{feature.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border">
                    <h3 className="font-semibold mb-3 border-b pb-2">Assigned ACCA Papers</h3>
                    {availablePapers.length === 0 ? (
                        <p className="text-sm text-gray-500">Loading papers...</p>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {availablePapers.map(paper => (
                                <label key={paper.id} className="flex items-center gap-3 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={assignedPapers.includes(paper.id)}
                                        onChange={() => togglePaper(paper.id)}
                                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                                    />
                                    <span className="text-gray-700">{paper.name}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 flex justify-end gap-4">
                <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
                    Cancel
                </button>
                <button 
                    onClick={handleSavePermissions}
                    disabled={loading}
                    className="px-6 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                >
                    {loading ? 'Saving...' : 'Save Permissions'}
                </button>
            </div>
        </div>
    );
};

const ConfirmationModal = ({ message, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full text-center">
            <p className="mb-6 text-gray-700">{message}</p>
            <div className="flex justify-center gap-4">
                <button
                    onClick={onCancel}
                    className="bg-gray-300 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-400"
                >
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600"
                >
                    Confirm
                </button>
            </div>
        </div>
    </div>
);

const SuperAdminDashboard = ({ user, setModal }) => {
    const { userData } = useAuth();
    const [view, setView] = useState('users'); 
    const [examMgmtView, setExamMgmtView] = useState('paperList'); 
    
    const [selectedPaper, setSelectedPaper] = useState(null);
    const [selectedExam, setSelectedExam] = useState(null);
    const [editingExam, setEditingExam] = useState(null);
    const [examToDelete, setExamToDelete] = useState(null);
    const [activeLecture, setActiveLecture] = useState(null);
    const [homeworkReviewData, setHomeworkReviewData] = useState(null);
    const [editingAdminPermissions, setEditingAdminPermissions] = useState(null);

    const handleEditExam = (exam) => {
        setEditingExam(exam);
        setExamMgmtView('examForm');
    };

    const handleDeleteExamRequest = (examId) => {
        setExamToDelete(examId);
    };

    const confirmDeleteExam = async () => {
        if (examToDelete) {
            try {
                await deleteDoc(doc(db, 'exams', examToDelete));
                logAuditAction(user, 'DELETE_EXAM', `Deleted exam with ID: ${examToDelete}`, { id: examToDelete });
                setModal({ show: true, message: 'Exam deleted successfully.' });
            } catch (error) {
                setModal({ show: true, message: `Error deleting exam: ${error.message}` });
            } finally {
                setExamToDelete(null);
            }
        }
    };

    const handleCreateNewExam = () => {
        setEditingExam(null);
        setExamMgmtView('examForm');
    };

    const handleViewExams = (paper) => {
        setSelectedPaper(paper);
        setExamMgmtView('examList');
    };
    
    const handleViewQuestionBank = (paper) => {
        setSelectedPaper(paper);
        setExamMgmtView('questionBank');
    };

    const handleViewLectures = (paper) => {
        setSelectedPaper(paper);
        setExamMgmtView('lectureManager');
    };

    const handleViewHomework = (paper) => {
        setSelectedPaper(paper);
        setExamMgmtView('homeworkManager');
    };

    // Added explicit Instil IQ routing interface for Super Admins
    const handleViewInstilIQ = (paper) => {
        setSelectedPaper(paper);
        setExamMgmtView('instilIQ');
    };
    
    const handleStartLiveClass = (lecture) => {
        setActiveLecture(lecture);
        setExamMgmtView('adminLiveRoom');
    };

    const handleViewSubmissions = (exam) => {
        setSelectedExam(exam);
        setExamMgmtView('examAttemptsList');
    };

    const handleViewAnalytics = (exam) => {
        setSelectedExam(exam);
        setExamMgmtView('examAnalytics');
    };

    const handleAllowRetake = async (attemptId) => {
        if (!window.confirm("Allow this student to retake the exam? This will let them start a new attempt.")) return;
        try {
            await updateDoc(doc(db, 'attempts', attemptId), { allowRetake: true });
            logAuditAction(user, 'ALLOW_RETAKE', `Allowed retake for attempt ID: ${attemptId}`, { attemptId });
            setModal({ show: true, message: "Retake allowed for student." });
        } catch (error) {
            console.error("Error allowing retake:", error);
            setModal({ show: true, message: "Failed to allow retake." });
        }
    };

    const renderExamManagement = () => {
        switch (examMgmtView) {
            case 'instilIQ':
                return <InstilIQManager
                            paper={selectedPaper}
                            user={user}
                            onBack={() => { setSelectedPaper(null); setExamMgmtView('paperList'); }}
                            setModal={setModal}
                        />;
            case 'examForm':
                return <CreateExam
                            user={user}
                            setModal={setModal}
                            examToEdit={editingExam}
                            paper={selectedPaper}
                            onFinishEditing={() => { setEditingExam(null); setExamMgmtView('examList'); }}
                        />;
            case 'examAttemptsList':
                return <ExamAttemptsList
                            exam={selectedExam}
                            paper={selectedPaper}
                            onBack={() => { setSelectedExam(null); setExamMgmtView('examList'); }}
                            setModal={setModal}
                            onAllowRetake={handleAllowRetake} 
                        />;
            case 'examAnalytics':
                return <ExamAnalytics
                            exam={selectedExam}
                            onBack={() => { setSelectedExam(null); setExamMgmtView('examList'); }}
                            setModal={setModal}
                        />;
            case 'questionBank':
                return <QuestionBankManager
                            paper={selectedPaper}
                            onBack={() => { setSelectedPaper(null); setExamMgmtView('paperList'); }}
                            setModal={setModal}
                        />;
            case 'lectureManager':
                return <LectureManager
                            paper={selectedPaper}
                            onBack={() => { setSelectedPaper(null); setExamMgmtView('paperList'); }}
                            setModal={setModal}
                            onStartLive={handleStartLiveClass} 
                        />;
            case 'homeworkManager':
                return <HomeworkManager
                            paper={selectedPaper}
                            onBack={() => { setSelectedPaper(null); setExamMgmtView('paperList'); }}
                            setModal={setModal}
                            onOpenHomework={(hw, student, sub) => {
                                setHomeworkReviewData({ hw, student, sub });
                                setExamMgmtView('homeworkReview');
                            }}
                        />;
            case 'homeworkReview':
                return <HomeworkRoom
                            homework={homeworkReviewData.hw}
                            user={user}
                            onBack={() => setExamMgmtView('homeworkManager')}
                            reviewMode={true}
                            reviewTargetStudent={homeworkReviewData.student}
                            reviewSubmission={homeworkReviewData.sub}
                        />;
            case 'adminLiveRoom':
                return <LectureRoom 
                            lecture={activeLecture} 
                            user={user} 
                            userRole={userData?.role}
                            mode="admin-live" 
                            onBack={() => { 
                                setActiveLecture(null); 
                                setExamMgmtView('lectureManager'); 
                            }} 
                        />;
            case 'examList':
                return <ExamListAdmin
                            paper={selectedPaper}
                            onEdit={handleEditExam}
                            onDelete={handleDeleteExamRequest}
                            onCreateNew={handleCreateNewExam}
                            onViewSubmissions={handleViewSubmissions}
                            onViewAnalytics={handleViewAnalytics} 
                            onBack={() => { setSelectedPaper(null); setExamMgmtView('paperList'); }}
                            setModal={setModal}
                        />;
            case 'paperList':
            default:
                return <PaperListAdmin 
                    onViewExams={handleViewExams} 
                    onViewQuestionBank={handleViewQuestionBank} 
                    onViewLectures={handleViewLectures}
                    onViewHomework={handleViewHomework} 
                    onViewInstilIQ={handleViewInstilIQ} // Attached to child triggers
                    setModal={setModal} 
                />;
        }
    };

    return (
        <div>
            {examToDelete && (
                <ConfirmationModal
                    message="Are you sure you want to delete this exam? This action cannot be undone."
                    onConfirm={confirmDeleteExam}
                    onCancel={() => setExamToDelete(null)}
                />
            )}
            
            <div className="flex gap-4 mb-6 border-b overflow-x-auto p-1">
                <button 
                    onClick={() => setView('users')} 
                    className={`py-2 px-4 font-semibold whitespace-nowrap rounded-t-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md ${
                        view === 'users' 
                        ? 'border-b-2 border-purple-600 text-purple-600 bg-purple-50' 
                        : 'text-gray-500 hover:text-purple-500 hover:bg-gray-50'
                    }`}
                >
                    User Management
                </button>
                <button 
                    onClick={() => setView('exams')} 
                    className={`py-2 px-4 font-semibold whitespace-nowrap rounded-t-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md ${
                        view === 'exams' 
                        ? 'border-b-2 border-purple-600 text-purple-600 bg-purple-50' 
                        : 'text-gray-500 hover:text-purple-500 hover:bg-gray-50'
                    }`}
                >
                    Exam & Content Management
                </button>
                <button 
                    onClick={() => setView('audit')} 
                    className={`py-2 px-4 font-semibold whitespace-nowrap rounded-t-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md ${
                        view === 'audit' 
                        ? 'border-b-2 border-purple-600 text-purple-600 bg-purple-50' 
                        : 'text-gray-500 hover:text-purple-500 hover:bg-gray-50'
                    }`}
                >
                    Audit Logs
                </button>
            </div>

            {view === 'users' && (
                 <div className="animate-fade-in">
                    {editingAdminPermissions ? (
                        <AdminPermissionManager 
                            adminUser={editingAdminPermissions} 
                            onClose={() => setEditingAdminPermissions(null)} 
                            setModal={setModal} 
                        />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <AddUserForm roleToAdd="admin" addedBy={user.uid} setModal={setModal} />
                                <UserList 
                                    roleToList="admin" 
                                    setModal={setModal} 
                                    onEditUser={(adminData) => setEditingAdminPermissions(adminData)} 
                                />
                            </div>
                            <div>
                                <BulkAddStudents addedBy={user.uid} setModal={setModal} />
                                <AddUserForm roleToAdd="student" addedBy={user.uid} setModal={setModal} />
                                <UserList roleToList="student" setModal={setModal} />
                            </div>
                        </div>
                    )}
                 </div>
            )}
            
            {view === 'exams' && (
                <div className="animate-fade-in">
                    {renderExamManagement()}
                </div>
            )}

            {view === 'audit' && (
                <div className="animate-fade-in">
                    <AuditTrail />
                </div>
            )}
        </div>
    );
};

export default SuperAdminDashboard;